import { memo, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import DataTable, {
  IDataTableColumn,
  IDataTableConditionalRowStyles,
} from 'react-data-table-component'
import { useHistory } from 'react-router-dom'
import { CS, CS_m } from 'back-end-api'

import LoaderMini from 'components/common/loaders/mini'
import { useTranslation } from 'services/Translation'
import {
  useStatusTranslation,
  isLockedByOtherUser,
  getRegistrationStatus,
  getSummaryStatusText,
  getSummaryStatusColor,
  getActiveAppointmentFromSummary,
} from 'services/Registrations'
import { getClaims } from 'services/Authentication'
import useResponsiveHp from 'hooks/useResponsiveHp'
import useDoctorMap from 'hooks/useDoctorMap'

import { error, transparency } from 'styles/Colors'

import { STATUS, ROUTES } from 'Constants'
import Lock from 'assets/icons/Lock'
import ArrayCell from './ArrayCell'
import TimeCell from './TimeCell'
import PickUp from './PickUp'
import IncomingCallIcon from './IncomingCallIcon'
import { DECLARED_ILL_FILTER, DECLARED_NOT_ILL_FILTER } from '../StatusFilter'
import { ColorDiv, StatusContainer, LockContainer, Container } from './Styles'

// Use the shared appointment selection logic from services/Registrations
const getActiveAppointment = (r: CS_m.ModelsRegistration): CS_m.ModelsAppointment | null => {
  const summary = CS.getSummary(r)
  return getActiveAppointmentFromSummary(summary)
}

// Helper function to get the correct date for a registration based on its status
// For submitted summaries, use summary.updated_at; otherwise use registration.created_at
const getRegistrationDisplayDate = (r: CS_m.ModelsRegistration): string => {
  const summary = CS.getSummary(r)
  const appointment = CS.getAppointment(summary)
  // If there's an appointment with a datetime, use that
  if (appointment?.datetime) {
    return appointment.datetime
  }
  // For summaries that have been updated (submitted), use summary.updated_at
  if (summary?.updated_at) {
    return summary.updated_at
  }
  // Fallback to registration creation date
  return r.created_at
}

const AV_STATUS_COLORS: Record<string, string> = {
  pending: '#8B5CF6',
  accepted: '#7C3AED',
  ready: '#06B6D4',
  inProgress: '#2563EB',
  done: '#10B981',
  declined: '#EF4444',
  cancelled: '#EF4444',
  timeout: '#DC2626',
  missed: '#B91C1C',
}

const AV_STATUS_LABEL: Record<string, string> = {
  [CS_m.ModelsAppointmentStatus.pending]: 'AppointmentPending',
  [CS_m.ModelsAppointmentStatus.accepted]: 'AppointmentAccepted',
  [CS_m.ModelsAppointmentStatus.ready]: 'AppointmentReady',
  [CS_m.ModelsAppointmentStatus.inProgress]: 'AppointmentInProgress',
  [CS_m.ModelsAppointmentStatus.done]: 'AppointmentDone',
  [CS_m.ModelsAppointmentStatus.declined]: 'AppointmentDeclined',
  [CS_m.ModelsAppointmentStatus.cancelled]: 'AppointmentCancelled',
  [CS_m.ModelsAppointmentStatus.timeout]: 'AppointmentTimeout',
  [CS_m.ModelsAppointmentStatus.missed]: 'AppointmentMissed',
}

export const conditionalRowStyles: IDataTableConditionalRowStyles<CS_m.ModelsRegistration>[] = [
  {
    when: (r: CS_m.ModelsRegistration) => isLockedByOtherUser(r),
    style: {
      opacity: 0.5,
      cursor: 'not-allowed !important',
    },
  },
  {
    when: (r: CS_m.ModelsRegistration) =>
      CS.getSummary(r)?.red_flags != null && CS.getSummary(r)!.red_flags!.length > 0,
    style: {
      boxShadow: `3px 6px 24px 0 ${error + transparency[10]} !important`,
    },
  },
  {
    when: (r: CS_m.ModelsRegistration): boolean =>
      CS.getSummary(r) != null && CS.isSummaryEmergency(CS.getSummary(r)!),
    style: {
      boxShadow: `0px 0px 16px 0 ${error + transparency[40]} !important`,
    },
  },
  {
    when: (r: CS_m.ModelsRegistration) => {
      return CS.getSummary(r)?.status === CS_m.ModelsSummaryStatus.inProgress
    },
    style: {
      cursor: 'default !important',
    },
  },
  // Flickering highlight for rows with a ready AV call belonging to another doctor
  {
    when: (r: CS_m.ModelsRegistration) => {
      const claims = getClaims()
      const currentDoctorId = claims?.sub ? parseInt(claims.sub, 10) : 0
      const appointment = CS.getAppointment(CS.getSummary(r))
      return (
        appointment?.status === CS_m.ModelsAppointmentStatus.ready &&
        !!appointment.doctor_account_id &&
        appointment.doctor_account_id !== currentDoctorId
      )
    },
    style: {
      animation: 'avCallFlicker 1.5s ease-in-out infinite',
      borderColor: '#F59E0B',
    },
  },
]

const ClickableCell = ({ children, onClick }: any) => (
  <div role="presentation" onClick={onClick} onKeyDown={onClick}>
    {children}
  </div>
)

interface ColumnStructure<T> {
  id: string
  name: string
  handleRowClicked: (r: T) => void
  cell: (r: T) => any
  sortable?: boolean
  // IMPORTANT: numeric comparator (not boolean)
  sortFunction?: (a: T, b: T) => number
  center?: boolean
  minWidth?: string
  width?: string
}

function createColumn<T>(column: ColumnStructure<T>): IDataTableColumn<T> {
  return {
    id: column.id,
    name: column.name,
    selector: column.id,
    cell: (r: T) => (
      <ClickableCell
        onClick={() => {
          column.handleRowClicked(r)
        }}
      >
        {column.cell(r)}
      </ClickableCell>
    ),
    sortable: column.sortable,
    // Pass numeric comparator straight through
    sortFunction: column.sortFunction,
    center: column.center,
    minWidth: column.minWidth,
    width: column.width,
  }
}

const columns = (
  t: (key: string) => string,
  tStatus: (key: string) => string,
  formatDate: (date: Date, formatString: string) => string,
  handleRowClicked: (r: CS_m.ModelsRegistration) => void,
  doctorMap: Map<number, string>
): IDataTableColumn<CS_m.ModelsRegistration>[] => [
  createColumn<CS_m.ModelsRegistration>({
    id: 'contact',
    name: t('patient'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => (
      <div
        data-tag="___react-data-table-allow-propagation___"
        data-cy="tableContact"
        className="w-full font-csc65 text-blue-900 flex justify-between items-center truncate"
        style={{ fontSize: '0.9375rem', paddingLeft: 24 }}
      >
        {`${r?.first_name} ${r?.last_name}`}
        <div className="w-px bg-grey-100" style={{ height: 33 }} />
      </div>
    ),
    sortable: true,
    // Sort by last_name, then first_name
    sortFunction: (a, b) => {
      const last = (a.last_name || '').localeCompare(b.last_name || '')
      if (last !== 0) return last
      return (a.first_name || '').localeCompare(b.first_name || '')
    },
    width: '16%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'complaints',
    name: t('complaints'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => CS.getSummary(r)?.complaint?.name ?? 'Medical question',
    sortable: true,
    sortFunction: (a, b) => {
      const ca = CS.getSummary(a)?.complaint?.name ?? 'Medical question'
      const cb = CS.getSummary(b)?.complaint?.name ?? 'Medical question'
      return ca.localeCompare(cb)
    },
    center: true,
    width: '13%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'age',
    name: t('age'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => r.age,
    sortable: true,
    sortFunction: (a, b) => (a.age ?? 0) - (b.age ?? 0),
    center: false,
    width: '6%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'gender',
    name: t('gender'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => <div className="pl-1.5">{r.gender}</div>,
    sortable: true,
    sortFunction: (a, b) => (a.gender || '').localeCompare(b.gender || ''),
    center: false,
    width: '7%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'redFlags',
    name: t('redFlags'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => <ArrayCell data={CS.getSummary(r)?.red_flags ?? []} />,
    sortable: true,
    sortFunction: (a, b) => {
      const la = CS.getSummary(a)?.red_flags?.length ?? 0
      const lb = CS.getSummary(b)?.red_flags?.length ?? 0
      return la - lb
    },
    center: true,
    width: '11%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'status',
    name: t('status'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => {
      const summary = CS.getSummary(r)
      const adviceIsRead = summary?.advice?.is_read === true
      return (
        <StatusContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
            <ColorDiv color={getSummaryStatusColor(r)}>{getSummaryStatusText(r, tStatus)}</ColorDiv>
            {adviceIsRead && (
              <span
                title={t('adviceRead')}
                style={{ display: 'inline-flex', alignItems: 'center', cursor: 'default', marginLeft: '8px' }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 0.5 }}
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </span>
            )}
          </div>
        </StatusContainer>
      )
    },
    sortable: true,
    // Lifecycle order: earliest statuses first (asc) → latest statuses first (desc) on toggle
    sortFunction: (a, b) => {
      const statusLifecycleOrder = [
        CS.RegistrationStatus.Registered,
        CS.RegistrationStatus.SummaryInProgress,
        CS.RegistrationStatus.SummarySubmitted,
        CS.RegistrationStatus.SummaryAdviceSent,
        CS.RegistrationStatus.SummaryReferralSent,
        CS.RegistrationStatus.SummaryReferralSpecialSent,
        CS.RegistrationStatus.AppointmentPending,
        CS.RegistrationStatus.AppointmentAccepted,
        CS.RegistrationStatus.AppointmentReady,
        CS.RegistrationStatus.AppointmentInProgress,
        CS.RegistrationStatus.AppointmentDone,
        CS.RegistrationStatus.SummaryConsulted,
        CS.RegistrationStatus.AppointmentDeclined,
        CS.RegistrationStatus.AppointmentCancelled,
        CS.RegistrationStatus.AppointmentTimeout,
        CS.RegistrationStatus.AppointmentMissed,
        CS.RegistrationStatus.SummaryCancelled,
        CS.RegistrationStatus.SummaryTimeout,
      ]

      const aIdx = statusLifecycleOrder.indexOf(getRegistrationStatus(a))
      const bIdx = statusLifecycleOrder.indexOf(getRegistrationStatus(b))
      if (aIdx !== bIdx) return aIdx - bIdx

      // Same status -> NEWEST first
      const ta = new Date(a.created_at).getTime()
      const tb = new Date(b.created_at).getTime()
      return tb - ta
    },
    width: '11%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'appointmentStatus',
    name: t('appointmentStatus'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => {
      const appt = getActiveAppointment(r)
      if (!appt) return <div style={{ textAlign: 'center', padding: '0 8px', color: '#9CA3AF', fontSize: '0.75rem' }}>—</div>
      const color = AV_STATUS_COLORS[appt.status as string] || '#8B5CF6'
      const label = tStatus(AV_STATUS_LABEL[appt.status as string] ?? '')
      const apptDate = appt.datetime ? new Date(appt.datetime) : null
      return (
        <div style={{ textAlign: 'center', padding: '0 4px', lineHeight: 1.3 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
            <span style={{ fontSize: '0.6875rem', color, fontFamily: 'coresanscr55, sans-serif' }}>{label}</span>
          </div>
          {apptDate && (
            <div style={{ fontSize: '0.625rem', color: '#6B7280', fontFamily: 'coresanscr45, sans-serif', marginTop: 2 }}>
              {formatDate(apptDate, 'dd MMM')}{' '}
              {formatDate(apptDate, 'HH:mm')}
            </div>
          )}
        </div>
      )
    },
    sortable: true,
    sortFunction: (a, b) => {
      const apptA = getActiveAppointment(a)
      const apptB = getActiveAppointment(b)
      if (!apptA && !apptB) return 0
      if (!apptA) return 1
      if (!apptB) return -1
      const statusOrder = ['pending', 'accepted', 'ready', 'inProgress', 'done', 'declined', 'cancelled', 'timeout', 'missed']
      return statusOrder.indexOf(apptA.status as string) - statusOrder.indexOf(apptB.status as string)
    },
    center: true,
    width: '11%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'time',
    name: t('time'),
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => (
      <div style={{ textAlign: 'center', padding: '0 8px' }}>
        <TimeCell time={getRegistrationDisplayDate(r)} />
      </div>
    ),
    sortable: true,
    // NEWEST first when sorting by time
    sortFunction: (a, b) =>
      new Date(getRegistrationDisplayDate(b)).getTime() - new Date(getRegistrationDisplayDate(a)).getTime(),
    width: '9%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'lock',
    name: '',
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => {
      const appointment = CS.getAppointment(CS.getSummary(r))
      const claims = getClaims()
      const currentDoctorId = claims?.sub ? parseInt(claims.sub, 10) : 0
      const hasIncomingCallForMe =
        !!appointment &&
        (appointment.status === CS_m.ModelsAppointmentStatus.ready ||
          appointment.status === CS_m.ModelsAppointmentStatus.inProgress) &&
        appointment.doctor_account_id === currentDoctorId
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {hasIncomingCallForMe && appointment && (
            <IncomingCallIcon appointment={appointment} registrationId={r.id} />
          )}
          {r.doctor_account_id && (() => {
            const drName = doctorMap.get(r.doctor_account_id!)
            return (
              <LockContainer className="lock-tooltip-wrapper">
                <Lock />
                {drName && (
                  <span className="lock-tooltip">
                    {t('lockedByDr').replace('%s', drName)}
                  </span>
                )}
              </LockContainer>
            )
          })()}
        </div>
      )
    },
    center: true,
    width: '10%',
  }),
  createColumn<CS_m.ModelsRegistration>({
    id: 'pickup',
    name: '',
    handleRowClicked,
    cell: (r: CS_m.ModelsRegistration) => (
      <>
        {(CS.getAppointment(CS.getSummary(r))?.status === CS_m.ModelsAppointmentStatus.ready ||
          CS.getAppointment(CS.getSummary(r))?.status ===
            CS_m.ModelsAppointmentStatus.inProgress) && (
          <PickUp appointment={CS.getAppointment(CS.getSummary(r))!} />
        )}
      </>
    ),
    center: true,
    width: '6%',
  }),
]

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    patient: getText('Patient contact information', 'Patient_List_Table'),
    complaints: getText('Complaint(s)', 'Patient_List_Table'),
    age: getText('Age', 'Patient_List_Table'),
    gender: getText('Gender', 'Patient_List_Table'),
    patientId: getText('Patient ID', 'Patient_List_Table'),
    redFlags: getText('Red Flags', 'Patient_List_Table'),
    status: getText('MIA Status', 'Patient_List_Table'),
    appointmentStatus: getText('AV Status', 'Patient_List_Table'),
    time: getText('Time of status', 'Patient_List_Table'),
    male: getText('Male', 'Patient_List_Table'),
    female: getText('Female', 'Patient_List_Table'),
    empty: getText('There are no records to display', 'Patient_List_Table'),
    adviceSent: getText('Advice sent', 'Patient_List_Table'),
    adviceRead: getText('Advice read', 'Patient_List_Table'),
    emergency: getText('Emergency', 'Patient_List_Table'),
    lockedByOther: getText('This patient is currently being viewed by another doctor.', 'Patient_List_Table'),
    lockedByDr: getText('Locked by dr. %s', 'Patient_List_Table'),
    viewedByDr: getText('This patient is currently being viewed by dr. %s.', 'Patient_List_Table'),
    viewReadOnly: getText('View Read-Only', 'Patient_List_Table'),
  }[key])

interface TableProps {
  open: boolean
  statusFilters: Set<number>
}

const Table = ({ open, statusFilters }: TableProps) => {
  const { t, formatDate } = useTranslation(texts)
  const tStatus = useStatusTranslation()
  const hp = useResponsiveHp()
  // Scale-compensated table body height (virtual px) so the table fills the
  // available space at any window size instead of leaving growing empty space.
  const tableHeight = open ? `${hp(100) - 176}px` : `${hp(100) - 336}px`
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>('status')
  const [sortDirection, setSortDirection] = useState(true)
  const [lockedPopupVisible, setLockedPopupVisible] = useState(false)
  const [lockedRegistrationId, setLockedRegistrationId] = useState<number | null>(null)
  const doctorMap = useDoctorMap()

  const { data, isLoading } = CS.useRegistrationsList({
    without_old_closed_summaries: 'true',
  })

  const lockedDoctorName = useMemo(() => {
    if (!lockedRegistrationId || !data) return ''
    const reg = data.find((r: CS_m.ModelsRegistration) => r.id === lockedRegistrationId)
    if (!reg?.doctor_account_id) return ''
    return doctorMap.get(reg.doctor_account_id) || ''
  }, [lockedRegistrationId, data, doctorMap])

  const filteredData = useMemo(() => {
    if (!data) return []

    // Hide "Registered" entries older than 24 hours
    const now = Date.now()
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000
    const withoutOldRegistered = data.filter(r => {
      if (getRegistrationStatus(r) !== CS.RegistrationStatus.Registered) return true
      return now - new Date(r.created_at).getTime() < TWENTY_FOUR_HOURS
    })

    if (statusFilters.size === 0) return withoutOldRegistered

    const wantIll = statusFilters.has(DECLARED_ILL_FILTER)
    const wantNotIll = statusFilters.has(DECLARED_NOT_ILL_FILTER)
    // Status filters minus the sick-label sentinels, matched against getRegistrationStatus
    const regStatusFilters = new Set(
      [...statusFilters].filter(v => v !== DECLARED_ILL_FILTER && v !== DECLARED_NOT_ILL_FILTER)
    )

    return withoutOldRegistered.filter(r => {
      const sickLabel = CS.getSummary(r)?.sick_label
      const matchesIll = wantIll && sickLabel === 'sick'
      const matchesNotIll = wantNotIll && sickLabel === 'not_sick'
      const matchesStatus = regStatusFilters.size > 0 && regStatusFilters.has(getRegistrationStatus(r))
      return matchesIll || matchesNotIll || matchesStatus
    })
  }, [data, statusFilters])

  const history = useHistory()

  const handleRowClicked = useCallback(
    (r: CS_m.ModelsRegistration) => {
      const summary = CS.getSummary(r)
      if (!summary || summary.status === STATUS.inProgress.value) {
        return
      }
      if (isLockedByOtherUser(r)) {
        setLockedRegistrationId(r.id)
        setLockedPopupVisible(true)
        return
      }
      history.push(`${ROUTES.PATIENT}/${r.id}`)
    },
    [history]
  )

  const handleSort = (e: IDataTableColumn<CS_m.ModelsRegistration>, direction: string) => {
    setSelectedColumn(e.selector as string)
    setSortDirection(direction === 'asc')
  }

  return (
    <>
      <Container height={tableHeight}>
        <DataTable
          noDataComponent={t('empty')}
          progressPending={isLoading}
          progressComponent={<LoaderMini />}
          columns={columns(t, tStatus, formatDate, handleRowClicked, doctorMap)}
          data={filteredData}
          noHeader
          fixedHeader
          fixedHeaderScrollHeight="unset"
          dense
          onRowClicked={handleRowClicked}
          conditionalRowStyles={conditionalRowStyles}
          defaultSortField={selectedColumn}
          defaultSortAsc={sortDirection}
          onSort={handleSort}
        />
      </Container>
      {lockedPopupVisible && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          role="presentation"
          onClick={() => setLockedPopupVisible(false)}
          onKeyDown={() => setLockedPopupVisible(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '2rem 2.5rem',
              maxWidth: '400px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              fontFamily: 'coresanscr55, sans-serif',
            }}
            role="presentation"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <div style={{ marginBottom: '0.5rem' }}>
              <Lock />
            </div>
            <p style={{ fontSize: '1rem', color: '#202336', lineHeight: 1.5, margin: '0 0 1.5rem' }}>
              {lockedDoctorName
                ? t('viewedByDr').replace('%s', lockedDoctorName)
                : t('lockedByOther')}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setLockedPopupVisible(false)}
                style={{
                  backgroundColor: '#fff',
                  color: '#202336',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontFamily: 'coresanscr65, sans-serif',
                  cursor: 'pointer',
                }}
              >
                OK
              </button>
              <button
                type="button"
                onClick={() => {
                  setLockedPopupVisible(false)
                  if (lockedRegistrationId) {
                    history.push(`${ROUTES.PATIENT}/${lockedRegistrationId}?readOnly=true`)
                  }
                }}
                style={{
                  backgroundColor: '#EB7C0C',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontFamily: 'coresanscr65, sans-serif',
                  cursor: 'pointer',
                }}
              >
                {t('viewReadOnly')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

Table.whyDidYouRender = true

export default memo(Table)
